import { Check, ChevronsUpDown, Pencil } from "lucide-react";
import { useMemo, useState } from "react";

import { ClientEditDialog } from "@/components/client-edit-dialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useClients } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ClientSelectProps {
  /** Currently assigned client id, or `null` for "Unassigned". */
  value: number | null;
  /** Called with the newly selected client id (or `null` to unassign). */
  onChange: (clientId: number | null) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Popover + Command combobox for picking the client a project is grouped
 * under. Presentational only — the caller owns what mutation, if any, fires
 * when `onChange` is called. Creating (and branding) a client lives on the
 * `/clients` page, not inline here, so this is a pure picker.
 */
export function ClientSelect({ value, onChange, disabled, className }: ClientSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [editingClientId, setEditingClientId] = useState<number | null>(null);

  const { data: clients = [], isLoading } = useClients();

  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return clients;
    }
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, query]);

  const selectedClient = value !== null ? (clients.find((c) => c.id === value) ?? null) : null;
  const label =
    value === null
      ? "Unassigned"
      : (selectedClient?.name ?? (isLoading ? "Loading..." : "Unknown"));

  function handleSelect(clientId: number | null) {
    onChange(clientId);
    setOpen(false);
    setQuery("");
  }

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setQuery("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn("min-w-0 justify-between gap-2 font-normal", className)}
          >
            <span className="min-w-0 truncate">{label}</span>
            <ChevronsUpDown className="text-muted-foreground size-3.5 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search clients..." value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandGroup>
                <CommandItem value="unassigned" onSelect={() => handleSelect(null)}>
                  <Check className={cn("size-4", value === null ? "opacity-100" : "opacity-0")} />
                  <span>Unassigned</span>
                </CommandItem>
                {filteredClients.map((client) => (
                  <CommandItem
                    key={client.id}
                    value={String(client.id)}
                    onSelect={() => handleSelect(client.id)}
                  >
                    <Check
                      className={cn("size-4", value === client.id ? "opacity-100" : "opacity-0")}
                    />
                    <span className="min-w-0 flex-1 truncate">{client.name}</span>
                    <span className="text-muted-foreground text-xs">{client.project_count}</span>
                    <button
                      type="button"
                      aria-label={`Edit ${client.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingClientId(client.id);
                      }}
                      className="text-muted-foreground hover:text-foreground -mr-1 shrink-0 rounded p-1"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ClientEditDialog
        clientId={editingClientId}
        open={editingClientId !== null}
        onOpenChange={(v) => {
          if (!v) {
            setEditingClientId(null);
          }
        }}
      />
    </>
  );
}
